import React from 'react';

interface InvoiceLine {
  amount: string | null;
  description: string;
  type: string;
}

interface InvoiceSection {
  type: string;
  lines: InvoiceLine[];
}

interface Invoice {
  sections: InvoiceSection[];
  type: string;
  due_at: number;
}

interface InvoiceDisplayProps {
  invoice: Invoice;
  title: string;
}

const InvoiceSectionDisplay = ({ section }: { section: InvoiceSection }) => {
  const renderLine = (line: InvoiceLine, index: number) => {
    const getLineClasses = () => {
      switch (line.type) {
        case 'title':
          return 'text-lg font-semibold text-gray-900 border-b border-gray-200 pb-3 mb-4';
        case 'product-title':
          return 'text-base font-medium text-gray-800 mb-1 mt-3 first:mt-0';
        case 'prorated':
          return 'text-sm text-gray-600 mb-0.5';
        case 'subscription':
          return 'text-sm text-gray-600 mb-0.5';
        case 'subtotal':
          return 'text-sm text-gray-700 border-t border-gray-200 pt-3 mt-4 mb-1';
        case 'sales-tax':
          return 'text-xs text-gray-500 mb-0.5';
        case 'total':
          return 'text-base font-semibold text-gray-900 border-t border-gray-300 pt-3 mt-3';
        case 'credit_card':
          return 'text-sm text-gray-600 bg-gray-50 p-3 rounded border-t border-gray-200 mt-4';
        default:
          return 'text-sm text-gray-600 mb-0.5';
      }
    };

    return (
      <div key={index} className={`${getLineClasses()}`}>
        <div className="flex flex-wrap justify-between items-start gap-2">
          <span className="flex-1 min-w-0 break-words">{line.description}</span>
          {line.amount && (
            <span className="font-medium whitespace-nowrap flex-shrink-0">
              {line.amount}
            </span>
          )}
        </div>
      </div>
    );
  };

  return <div className="mb-4">{section.lines.map(renderLine)}</div>;
};

export const InvoiceDisplay = ({ invoice, title }: InvoiceDisplayProps) => {
  if (!invoice || !invoice.sections) {
    return null;
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
      <h4 className="text-lg font-semibold text-gray-900 mb-4">{title}</h4>
      {invoice.sections.map((section, index) => (
        <InvoiceSectionDisplay key={index} section={section} />
      ))}
    </div>
  );
};

export type { Invoice, InvoiceLine, InvoiceSection };
